import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { getClientBuildCommit } from "@/lib/buildIdentity";
import { trpc } from "@/lib/trpc";
import {
  applicationAreaFromRoute,
  buildStaffForegroundIssueNote,
  extractHttpStatus,
  extractTraceId,
  normalizeSafeErrorCategory,
  redactStaffRoute,
  singaporeTimestamp,
} from "@shared/staffForegroundIssue";
import { AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";

export type StaffForegroundErrorProps = {
  route: string;
  operation: string;
  error: unknown;
  incidentId: string;
  onRetry: () => void;
};

export function StaffForegroundError({ route, operation, error, incidentId, onRetry }: StaffForegroundErrorProps) {
  const { user } = useAuth();
  const [retryAttempted, setRetryAttempted] = useState(false);
  const [reportFailed, setReportFailed] = useState(false);
  const [submitLock, setSubmitLock] = useState(false);
  const report = trpc.system.reportStaffForegroundIssue.useMutation({
    meta: { background: true },
    onError: () => {
      setReportFailed(true);
      setSubmitLock(false);
    },
  });
  const safeRoute = redactStaffRoute(route);
  const category = normalizeSafeErrorCategory(error);
  const note = useMemo(
    () =>
      buildStaffForegroundIssueNote({
        incidentId,
        staffName: user?.name ?? user?.email ?? null,
        area: applicationAreaFromRoute(safeRoute),
        operation,
        httpStatus: extractHttpStatus(error),
        traceId: extractTraceId(error),
        singaporeAt: singaporeTimestamp(),
        browser: typeof navigator === "undefined" ? null : navigator.userAgent,
        navigationContext: typeof window === "undefined" ? safeRoute : window.location.pathname,
        retryAttempted,
        stackSnippet: error instanceof Error ? error.stack ?? error.message : String(error ?? ""),
      }),
    [incidentId, user?.name, user?.email, safeRoute, operation, error, retryAttempted],
  );

  const reference = report.data?.reportUuid;
  const sent = Boolean(reference) && !reportFailed;

  return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center" data-testid="staff-foreground-error">
      <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-red-700" />
      <h2 className="text-xl font-semibold text-[#172442]">This page could not be loaded</h2>
      <p className="mt-2 text-sm text-slate-600">
        The request failed, so this screen is not showing empty or zero data. You can retry or send an internal error
        report.
      </p>
      <p className="mt-3 font-mono text-xs text-slate-500">Reference {incidentId}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setRetryAttempted(true);
            onRetry();
          }}
        >
          Retry
        </Button>
        <Button
          type="button"
          disabled={report.isPending || sent || submitLock}
          onClick={() => {
            if (sent || report.isPending || submitLock) return;
            setReportFailed(false);
            setSubmitLock(true);
            report.mutate({
              route: safeRoute,
              operation: operation.slice(0, 128) || "page_load",
              safeErrorCode: category,
              note,
              clientIdempotencyKey: incidentId.replace(/[^A-Za-z0-9_-]/g, "").padEnd(8, "0").slice(0, 64),
              retryAttempted,
            });
          }}
        >
          {report.isPending ? "Sending…" : "Send Error Report"}
        </Button>
      </div>
      {sent ? (
        <p className="mt-4 text-sm text-emerald-800" data-testid="error-report-sent">
          Error report sent — reference {reference}
        </p>
      ) : null}
      {reportFailed && !sent ? (
        <p className="mt-4 text-sm text-red-700">The error report could not be sent. The original error is unchanged.</p>
      ) : null}
      <p className="mt-4 hidden text-xs text-slate-400" data-testid="error-report-build">
        {getClientBuildCommit()}
      </p>
    </div>
  );
}
